import { inject, Injectable } from '@angular/core';
import { assert, isDefined } from '@sama/js-core';
import { environment } from '../../../../environments/environment';
import { projectTaskPath } from '../../../cross-team-shared/hub-paths';
import {
  DeleteFieldFeedbackOperation,
  UpsertFieldFeedbackOperation,
} from '../../../team-apis/otterdam/feedback-operations/field-feedback-operations';
import {
  DeleteObjectFeedbackOperation,
  UpsertObjectFeedbackOperation,
} from '../../../team-apis/otterdam/feedback-operations/object-feedback-operations';
import {
  DeleteShapeFeedbackOperation,
  UpsertShapeFeedbackOperation,
} from '../../../team-apis/otterdam/feedback-operations/shape-feedback-operations';
import { Project } from '../../../team-apis/otterdam/project';
import { Submission } from '../../../team-apis/otterdam/submission';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { AbstractResource } from '../../http/abstract-resource';
import {
  getContext,
  SamplingWorkspaceStore,
} from '../../sampling-workspace/state/sampling-workspace-store';

export type FeedbackOperation =
  | UpsertObjectFeedbackOperation
  | DeleteObjectFeedbackOperation
  | UpsertFieldFeedbackOperation
  | DeleteFieldFeedbackOperation
  | UpsertShapeFeedbackOperation
  | DeleteShapeFeedbackOperation;

export type FeedbackContext =
  | 'external_spot_check'
  | 'external_qa'
  | 'internal_spot_check'
  | 'internal_qa'
  | 'qa_step'
  | '_latest';

export interface FeedbackRequestOptions {
  projectId: number;
  taskId: string;
  round: number;
  context: FeedbackContext;
}

@Injectable()
export class FeedbackResource extends AbstractResource {
  private store = inject(TaskWorkspaceStore);
  private samplingWorkspaceStore = inject(SamplingWorkspaceStore);

  sendOperations$(operations: FeedbackOperation[]) {
    const options = this.getRequestOptions();
    return this.request$<void>('POST', feedbackPath(options), {
      body: { operations },
      skipDefaultErrorMessaging: true,
      tokenScope: 'access_token',
    });
  }

  getRequestOptions(submission?: Submission): FeedbackRequestOptions {
    const { task, project } = this.store.state();

    return {
      projectId: project.id,
      taskId: task.id,
      round: this.getRound(submission),
      context: this.getContext(submission),
    };
  }

  private getRound(submission?: Submission) {
    const { workspace } = this.store.state();

    assert(
      workspace !== 'preview' && workspace !== 'login',
      'Preview workspace should not load feedback',
    );

    if (workspace === 'sampling') {
      return this.samplingWorkspaceStore.state().master.round;
    }

    if (workspace === 'readOnly' && isDefined(submission)) {
      return submission.round;
    }

    return Math.max(this.store.state().task.round - 1, 1);
  }

  private getContext(submission?: Submission) {
    const { workspace } = this.store.state();

    switch (workspace) {
      case 'production':
        return '_latest';
      case 'sampling':
        return getContext(this.samplingWorkspaceStore.state().master);
      case 'readOnly':
        return this.getReadOnlyContext(submission);
      default:
        throw new Error('Invalid workspace');
    }
  }

  private getReadOnlyContext(submission?: Submission) {
    if (!isDefined(submission)) {
      return '_latest';
    }

    const { stepId, project } = this.store.state();
    assert(stepId, 'stepId must be defined when submission is defined');

    return isQaStep(project, stepId) ? 'external_qa' : 'qa_step';
  }
}

export function feedbackPath(
  { projectId, taskId, round, context }: FeedbackRequestOptions,
  typesFilterQuery?: Array<'shape' | 'field' | 'object'>,
) {
  const feedbackPathURI = `${environment.annotationStoreServiceBaseUrl}${projectTaskPath(
    projectId,
    taskId,
  )}/rounds/${round}/contexts/${context}/feedbacks`;

  if (typesFilterQuery?.length) {
    return `${feedbackPathURI}?types=${typesFilterQuery.join(',')}`;
  }

  return feedbackPathURI;
}

export function isQaStep(project: Project, stepId: number) {
  return project.steps.find((step) => step.id === stepId)?.step_type === 'QA';
}
